import { faArrowUpRightFromSquare, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, useMemo } from 'react';
import { Link } from 'react-router-dom';

import MemberEligibilityBadges from '@/components/MemberEligibilityBadges';
import SessionAttendanceChips from '@/components/SessionAttendanceChips';
import type { Props } from '@/components/TreatmentGroupMembersList/types';
import { DAY_LABELS } from '@/constants/dayOfWeek';
import useTreatmentGroupMemberEligibility from '@/hooks/useTreatmentGroupMemberEligibility';
import type { TreatmentGroupMembership } from '@/types/treatmentGroup';
import { Avatar, Typography } from '@/ui';
import { formatCalendarDate, formatShortDateTime, formatTimeOfDay } from '@/utils/dates';
import { displayAsOf, sessionsInEffectAsOf } from '@/utils/treatmentGroupSessions';

const noop = () => {};

const enrollmentAttribution = ({ enrolled_at, enrolled_by_name }: TreatmentGroupMembership) => {
  if (!enrolled_at && !enrolled_by_name) return null;
  const by = enrolled_by_name ? ` by ${enrolled_by_name}` : '';
  const at = enrolled_at ? ` on ${formatShortDateTime(enrolled_at)}` : '';
  return `Added${by}${at}`;
};

const TreatmentGroupMembersList: FC<Props> = ({ group, memberActions }) => {
  const personIds = group.members.map((member) => member.person_id);
  const staffIds = [...new Set(group.sessions.map((session) => session.staff_id))];
  const { data: eligibilityByPersonId } = useTreatmentGroupMemberEligibility({
    appointmentTypeId: group.appointment_type_id,
    personIds,
    staffIds,
    treatmentGroupId: group.id
  });
  // Scoped to the group's currently-effective sessions -- an already-ended
  // (or not-yet-started) session isn't part of a member's current
  // attending_group_session_ids either, so showing it here would render as
  // a permanently unselected chip instead of being left off the list
  // entirely. Uses displayAsOf, not just this week, so a group whose start
  // date is still in the future keeps showing its intended schedule.
  const sessionOptions = useMemo(
    () =>
      sessionsInEffectAsOf(group.sessions, displayAsOf(group)).map((session) => ({
        id: session.id,
        label: `${DAY_LABELS[session.display_day_of_week]} · ${formatTimeOfDay(session.display_start_time)}`
      })),
    [group]
  );

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <Typography variant="label" muted>
          Members
        </Typography>
        <span className="flex items-center gap-1 text-uniform-gray-500">
          <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
          <Typography variant="body" className="text-sm">
            {group.active_member_count}/{group.capacity}
          </Typography>
        </span>
      </div>
      <ul className="divide-y divide-uniform-gray-200 dark:divide-uniform-gray-700">
        {group.members.map((member) => {
          const status = eligibilityByPersonId?.[member.person_id];
          const attribution = enrollmentAttribution(member);
          return (
            <li key={member.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
              <Link to={`/people/${member.person_id}`} className="shrink-0">
                <Avatar name={member.person_name} size="sm" />
              </Link>
              <div>
                <Link
                  to={`/people/${member.person_id}`}
                  className="group inline-flex items-center gap-1.5">
                  <Typography variant="body" className="font-medium group-hover:underline">
                    {member.person_name}
                  </Typography>
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="h-3 w-3 text-uniform-gray-400 group-hover:text-uniform-gray-600 dark:group-hover:text-uniform-gray-300"
                  />
                </Link>
                <Typography variant="body" muted className="text-sm">
                  Since {formatCalendarDate(member.start_on)}
                </Typography>
                {attribution && (
                  <Typography variant="body" muted className="text-xs">
                    {attribution}
                  </Typography>
                )}
                <MemberEligibilityBadges
                  ineligibilityReason={status?.ineligibility_reason}
                  isCredentialed={status?.is_credentialed}
                  isEligible={status?.is_eligible}
                  isExisting
                />
                {sessionOptions.length > 0 && (
                  <SessionAttendanceChips
                    className="mt-1.5"
                    disabled
                    label={
                      <span className="text-xs font-semibold uppercase tracking-wide text-uniform-gray-500 dark:text-uniform-gray-400">
                        Attending Sessions
                      </span>
                    }
                    onChange={noop}
                    selectedIds={member.attending_group_session_ids}
                    sessions={sessionOptions}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {memberActions && (
        <div className="mt-3 flex justify-start border-t border-uniform-gray-200 pt-3 dark:border-uniform-gray-700">
          {memberActions}
        </div>
      )}
    </section>
  );
};

export default TreatmentGroupMembersList;
